/**
 * AIAppraisal — Tính năng AI Thẩm định Chiến lược Quỹ
 *
 * Flow:
 *   Nhấn nút → thử gọi /api/appraise (Vercel Edge Function)
 *   Thành công → hiện Modal với phân tích Markdown từ Claude
 *   Thất bại (chưa deploy / chưa có API key) → fallback: Modal chứa prompt
 *     sẵn để user tự copy vào Claude.ai / ChatGPT
 */

import React, { useState } from 'react';
import { Sparkles, Copy, Check, X, AlertCircle, FileText } from 'lucide-react';
import { TargetRow } from './SAAPlanner';
import { formatVND } from '../utils/format';

// ── Dữ liệu đầu vào cho quá trình thẩm định ────────────────────────────────
export interface AppraisalData {
  fundName:       string;
  fundNAV:        number;   // NAV thực tế hiện tại
  targetNAV:      number;   // Target NAV (NAV tham chiếu trong bảng SAA)
  targets:        TargetRow[];
  blendedYield:   number;   // % yield kế hoạch
  blendedCap:     number;   // % cap gain kế hoạch
  blendedTotal:   number;   // tổng tỷ suất kế hoạch
  fireWithdrawal: number;   // VND rút mỗi năm
  fireInflation:  number;   // % lạm phát kỳ vọng
  finalNAV30:     number;   // NAV còn lại sau 30 năm (0 nếu cạn)
  depletionYear?: number;   // năm cạn vốn (nếu có)
}

// ── Master Prompt Builder ────────────────────────────────────────────────────
export function buildAppraisalPrompt(d: AppraisalData): string {
  const tableRows = d.targets.map(t =>
    `| ${t.assetClassName} | ${t.weight.toFixed(1)}% | ${formatVND(t.value)} | ${t.expYield.toFixed(1)}% | ${t.expCap.toFixed(1)}% | ${(t.expYield + t.expCap).toFixed(1)}% |`
  ).join('\n');

  const refNAV       = d.targetNAV > 0 ? d.targetNAV : d.fundNAV;
  const yieldVND     = formatVND(refNAV * d.blendedYield  / 100);
  const capVND       = formatVND(refNAV * d.blendedCap    / 100);
  const totalVND     = formatVND(refNAV * d.blendedTotal  / 100);
  const fireResult   = d.depletionYear
    ? `⚠ Cạn vốn vào năm thứ ${d.depletionYear}`
    : `✓ Còn lại ${formatVND(d.finalNAV30)} sau 30 năm`;

  return `Bạn là CIO (Chief Investment Officer) AI với 20 năm kinh nghiệm quản lý quỹ tại thị trường Việt Nam. Tôi cần bạn thẩm định toàn diện kế hoạch đầu tư chiến lược (SAA) dưới đây. Hãy phân tích với tư duy của một nhà quản lý quỹ chuyên nghiệp — thực tế, thẳng thắn, không né tránh điểm yếu.

---

## THÔNG TIN QUỸ

- **Tên Quỹ:** ${d.fundName}
- **NAV Thực tế (hiện tại):** ${formatVND(d.fundNAV)}
- **Target NAV (NAV tham chiếu):** ${d.targetNAV > 0 ? formatVND(d.targetNAV) : 'Chưa đặt mục tiêu'}

---

## CƠ CẤU TÀI SẢN CHIẾN LƯỢC (SAA)

| Lớp Tài Sản | Tỷ Trọng | Giá Trị Mục Tiêu | Yield | Cap Gain | Tổng Return |
|---|---|---|---|---|---|
${tableRows}

**Tổng hợp hiệu suất kế hoạch:**
- Blended Yield Rate: ${d.blendedYield.toFixed(2)}%/năm → ${yieldVND}/năm
- Blended Capital Gain: ${d.blendedCap.toFixed(2)}%/năm → ${capVND}/năm
- **Tổng Tỷ Suất Kế Hoạch: ${d.blendedTotal.toFixed(2)}%/năm → ${totalVND}/năm**

---

## DỰ PHÓNG DÒNG TIỀN 30 NĂM (FIRE Simulation)

- **NAV Khởi Đầu:** ${formatVND(d.fundNAV)}
- **Mức Rút Tiền Hàng Năm:** ${formatVND(d.fireWithdrawal)} (tăng theo lạm phát mỗi năm)
- **Tỷ Lệ Lạm Phát Kỳ Vọng:** ${d.fireInflation}%/năm
- **Tỷ Suất Sinh Lời Áp Dụng:** ${d.blendedTotal.toFixed(2)}%/năm
- **Kết Quả Sau 30 Năm:** ${fireResult}

---

## YÊU CẦU THẨM ĐỊNH

Dựa trên dữ liệu trên, hãy cung cấp phân tích chuyên sâu theo cấu trúc sau:

### 1. ĐÁNH GIÁ CƠ CẤU TÀI SẢN (Asset Allocation Review)
Nhận xét về tỷ trọng từng lớp tài sản: có hợp lý với mục tiêu FIRE không? Phân bổ rủi ro ra sao? So sánh với các chuẩn mực phân bổ tài sản tại Việt Nam.

### 2. ĐÁNH GIÁ TÍNH BỀN VỮNG FIRE
Với tỷ suất ${d.blendedTotal.toFixed(2)}%/năm và rút ${formatVND(d.fireWithdrawal)}/năm, kế hoạch này có đủ bền vững 30 năm không? Tỷ lệ rút vốn an toàn (Safe Withdrawal Rate) phù hợp với bối cảnh này là bao nhiêu?

### 3. RỦI RO CHÍNH (Top 3–5 Risks)
Liệt kê và phân tích các rủi ro đáng chú ý nhất, bao gồm cả rủi ro thị trường, lạm phát, thanh khoản, tập trung tài sản.

### 4. KHUYẾN NGHỊ ĐIỀU CHỈNH
Đề xuất cụ thể (nếu cần): thay đổi tỷ trọng, điều chỉnh tỷ suất kỳ vọng, thay đổi mức rút vốn, bổ sung loại tài sản mới.

### 5. ĐÁNH GIÁ TỔNG THỂ
Cho điểm từ 1–10 kèm nhận xét tổng kết ngắn gọn (2–3 câu).

---
*Phân tích bằng Tiếng Việt, chuyên nghiệp. Dùng số liệu cụ thể khi đưa ra nhận định.*`;
}

// ── Simple Markdown → JSX renderer ──────────────────────────────────────────
function parseBold(text: string): React.ReactNode[] {
  return text.split(/(\*\*[^*]+\*\*)/).map((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**')) {
      return <strong key={i} style={{ color: 'var(--tx-1)', fontWeight: 700 }}>{seg.slice(2, -2)}</strong>;
    }
    return seg;
  });
}

function MarkdownBlock({ text }: { text: string }) {
  const lines = text.split('\n');
  const nodes: React.ReactNode[] = [];
  let listBuffer: React.ReactNode[] = [];

  const flushList = () => {
    if (listBuffer.length > 0) {
      nodes.push(
        <ul key={`ul-${nodes.length}`} style={{ paddingLeft: 18, margin: '6px 0', display: 'flex', flexDirection: 'column', gap: 3 }}>
          {listBuffer}
        </ul>
      );
      listBuffer = [];
    }
  };

  lines.forEach((line, i) => {
    // Separator
    if (line === '---' || line === '***') {
      flushList();
      nodes.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid var(--bd)', margin: '14px 0' }} />);
      return;
    }
    // H3
    if (line.startsWith('### ')) {
      flushList();
      nodes.push(
        <div key={i} style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa', marginTop: 16, marginBottom: 6, letterSpacing: '0.02em' }}>
          {parseBold(line.slice(4))}
        </div>
      );
      return;
    }
    // H2
    if (line.startsWith('## ')) {
      flushList();
      nodes.push(
        <div key={i} style={{ fontSize: 14, fontWeight: 800, color: 'var(--tx-1)', marginTop: 18, marginBottom: 8, letterSpacing: '-0.01em' }}>
          {parseBold(line.slice(3))}
        </div>
      );
      return;
    }
    // H1
    if (line.startsWith('# ')) {
      flushList();
      nodes.push(
        <div key={i} style={{ fontSize: 16, fontWeight: 800, color: 'var(--tx-1)', marginTop: 18, marginBottom: 10 }}>
          {parseBold(line.slice(2))}
        </div>
      );
      return;
    }
    // Bullet list
    if (line.startsWith('- ') || line.startsWith('* ')) {
      listBuffer.push(
        <li key={i} style={{ fontSize: 13, color: 'var(--tx-2)', lineHeight: 1.6 }}>
          {parseBold(line.slice(2))}
        </li>
      );
      return;
    }
    // Numbered list
    const numMatch = line.match(/^(\d+)\.\s(.+)/);
    if (numMatch) {
      listBuffer.push(
        <li key={i} style={{ fontSize: 13, color: 'var(--tx-2)', lineHeight: 1.6 }}>
          <strong style={{ color: 'var(--tx-3)', marginRight: 4 }}>{numMatch[1]}.</strong>
          {parseBold(numMatch[2])}
        </li>
      );
      return;
    }
    // Table row — skip (tables come through as pre-formatted text, render as-is)
    if (line.startsWith('|')) {
      flushList();
      nodes.push(
        <div key={i} style={{ fontSize: 12, color: 'var(--tx-3)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.8, fontFamily: 'monospace, monospace' }}>
          {line}
        </div>
      );
      return;
    }
    // Empty line
    if (line.trim() === '') {
      flushList();
      nodes.push(<div key={i} style={{ height: 6 }} />);
      return;
    }
    // Normal paragraph
    flushList();
    nodes.push(
      <p key={i} style={{ fontSize: 13, color: 'var(--tx-2)', lineHeight: 1.7, margin: '2px 0' }}>
        {parseBold(line)}
      </p>
    );
  });

  flushList();
  return <>{nodes}</>;
}

// ── Main Button + Modal Component ────────────────────────────────────────────
export function AIAppraisalButton({ data }: { data: AppraisalData }) {
  const [state,    setState]    = useState<'idle' | 'loading' | 'result' | 'prompt'>('idle');
  const [result,   setResult]   = useState('');
  const [error,    setError]    = useState('');
  const [copied,   setCopied]   = useState(false);

  const prompt = buildAppraisalPrompt(data);

  // ── Gọi API ──────────────────────────────────────────────────────────────
  const runAppraisal = async () => {
    setState('loading');
    setError('');
    try {
      const res = await fetch('/api/appraise', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt }),
      });
      const json = await res.json();
      if (!res.ok || json.error) throw new Error(json.error ?? `HTTP ${res.status}`);
      setResult(json.result);
      setState('result');
    } catch (err: any) {
      // Fallback: hiện prompt để user tự dùng
      setError(err?.message ?? 'Không thể kết nối tới AI service.');
      setState('prompt');
    }
  };

  // ── Copy Prompt ───────────────────────────────────────────────────────────
  const copyPrompt = () => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const close = () => { setState('idle'); setResult(''); setError(''); };

  return (
    <>
      {/* ── Button ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4 }}>
        <button
          onClick={runAppraisal}
          disabled={state === 'loading' || data.targets.length === 0}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '11px 22px', borderRadius: 11, fontSize: 13, fontWeight: 700,
            background: data.targets.length === 0
              ? 'var(--bd)'
              : 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #6366f1 100%)',
            border: 'none',
            color: data.targets.length === 0 ? 'var(--tx-5)' : '#fff',
            cursor: data.targets.length === 0 ? 'not-allowed' : state === 'loading' ? 'wait' : 'pointer',
            boxShadow: data.targets.length === 0 ? 'none' : '0 4px 20px rgba(124,58,237,0.4)',
            transition: 'opacity 0.15s',
            opacity: state === 'loading' ? 0.8 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {state === 'loading' ? (
            <>
              <div style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite' }} />
              Đang thẩm định...
            </>
          ) : (
            <>
              <Sparkles size={14} />
              AI Thẩm định Kế hoạch
            </>
          )}
        </button>
      </div>

      {/* ══ Modal: Kết quả AI ══ */}
      {state === 'result' && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backgroundColor: 'var(--bg-overlay)', backdropFilter: 'blur(8px)' }}
          onClick={close}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 680,
              maxHeight: '90dvh',
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--bd)',
              borderRadius: '20px 20px 0 0',
              display: 'flex', flexDirection: 'column',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--bd)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: 'linear-gradient(135deg, #7c3aed20 0%, #a855f720 100%)', border: '1px solid #7c3aed30', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Sparkles size={15} color="#a855f7" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-1)' }}>AI Thẩm định Chiến lược</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1 }}>Phân tích bởi Claude · {data.fundName}</div>
                </div>
              </div>
              <button onClick={close} style={{ display: 'flex', padding: 8, borderRadius: 8, background: 'var(--bg-card-el)', border: '1px solid var(--bd)', cursor: 'pointer', color: 'var(--tx-4)' }}>
                <X size={16} />
              </button>
            </div>

            {/* Body scrollable */}
            <div style={{ overflowY: 'auto', padding: '20px 20px 0', flex: 1, WebkitOverflowScrolling: 'touch' as any }}>
              <MarkdownBlock text={result} />
            </div>

            {/* Footer: disclaimer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--bd)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.2)' }}>
                <AlertCircle size={13} color="#fbbf24" style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 11, color: 'var(--tx-4)', lineHeight: 1.5 }}>
                  Đây là phân tích mang tính tham khảo, không phải lời khuyên đầu tư. Mọi quyết định tài chính cần được đánh giá bởi chuyên gia được cấp phép.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ Modal: Prompt Fallback ══ */}
      {state === 'prompt' && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', backgroundColor: 'var(--bg-overlay)', backdropFilter: 'blur(8px)' }}
          onClick={close}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 680,
              maxHeight: '90dvh',
              backgroundColor: 'var(--bg-card)',
              border: '1px solid var(--bd)',
              borderRadius: '20px 20px 0 0',
              display: 'flex', flexDirection: 'column',
              paddingBottom: 'env(safe-area-inset-bottom, 0px)',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--bd)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 32, height: 32, borderRadius: 10, background: 'rgba(99,102,241,0.12)', border: '1px solid rgba(99,102,241,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FileText size={15} color="#6366f1" />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--tx-1)' }}>Prompt Thẩm định</div>
                  <div style={{ fontSize: 11, color: 'var(--tx-4)', marginTop: 1 }}>
                    {error ? `AI service chưa sẵn sàng — dán prompt này vào Claude.ai hoặc ChatGPT` : 'Copy và dán vào Claude.ai / ChatGPT'}
                  </div>
                </div>
              </div>
              <button onClick={close} style={{ display: 'flex', padding: 8, borderRadius: 8, background: 'var(--bg-card-el)', border: '1px solid var(--bd)', cursor: 'pointer', color: 'var(--tx-4)' }}>
                <X size={16} />
              </button>
            </div>

            {/* Error notice (nếu có) */}
            {error && (
              <div style={{ margin: '12px 20px 0', padding: '10px 14px', borderRadius: 10, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)', display: 'flex', alignItems: 'flex-start', gap: 8, flexShrink: 0 }}>
                <AlertCircle size={13} color="#f87171" style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ fontSize: 11, color: '#f87171', lineHeight: 1.5 }}>
                  <strong>Không thể kết nối AI tự động:</strong> {error}
                  <br />
                  <span style={{ color: 'var(--tx-4)' }}>Hướng dẫn: Copy prompt bên dưới → mở Claude.ai hoặc ChatGPT → dán vào và gửi.</span>
                </div>
              </div>
            )}

            {/* Prompt text scrollable */}
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', margin: '12px 20px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexShrink: 0 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--tx-4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Master Prompt ({prompt.length.toLocaleString('vi-VN')} ký tự)
                </span>
                <button
                  onClick={copyPrompt}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    background: copied ? 'rgba(16,185,129,0.12)' : 'var(--bg-card-el)',
                    border: `1px solid ${copied ? 'rgba(16,185,129,0.3)' : 'var(--bd)'}`,
                    color: copied ? '#10b981' : 'var(--tx-3)',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  {copied ? <><Check size={13} /> Đã copy!</> : <><Copy size={13} /> Copy Prompt</>}
                </button>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' as any }}>
                <pre style={{
                  fontSize: 11, lineHeight: 1.65, color: 'var(--tx-3)',
                  backgroundColor: 'var(--bg-card-el)', border: '1px solid var(--bd)',
                  borderRadius: 10, padding: '14px 16px',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  margin: 0, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                }}>
                  {prompt}
                </pre>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid var(--bd)', flexShrink: 0, marginTop: 12 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.2)' }}>
                <AlertCircle size={13} color="#fbbf24" style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ fontSize: 11, color: 'var(--tx-4)', lineHeight: 1.5 }}>
                  Đây là phân tích mang tính tham khảo, không phải lời khuyên đầu tư. Mọi quyết định tài chính cần được đánh giá bởi chuyên gia được cấp phép.
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
